import React, { useState, useMemo } from 'react';
import { useTask } from '../contexts/TaskContext';

export default function FinishDayModal({ isOpen, onClose }) {
  const { tasks, appDate, finishDay, getRecurringDueOn } = useTask();
  const [freeText, setFreeText] = useState('');
  const [copied, setCopied] = useState(false);
  const [allTodayCompleted, setAllTodayCompleted] = useState(false);
  const [step, setStep] = useState('edit'); // 'edit' | 'select' | 'preview' | 'confirm' | 'done'
  const [selectedTomorrowIds, setSelectedTomorrowIds] = useState([]);
  // アウトプットから除外するタスクID（プライベートなタスクを非表示にする）
  const [excludedOutputIds, setExcludedOutputIds] = useState([]);

  const completedToday = useMemo(
    () => tasks.filter((t) => t.category === 'today' && t.completed),
    [tasks]
  );
  const completedFuture = useMemo(
    () => tasks.filter((t) => t.category === 'future' && t.completed),
    [tasks]
  );

  // フォールバックもローカル日付を使う（toISOStringはUTCで最大9時間ズレるため使わない）
  const safeAppDate = useMemo(() => {
    if (appDate) return appDate;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [appDate]);

  // 「明日」はアプリ日付+1日で計算する（実時刻は使わない）。
  // これにより実時刻が0時を跨いでいても、アプリ日付6/13の日締めでは
  // 必ず6/14が「明日」になり、6/15にズレない。
  const tomorrowDate = useMemo(() => {
    const parts = safeAppDate.split('-');
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [safeAppDate]);

  const autoTomorrowTasks = useMemo(
    () => tasks.filter((t) => t.category === 'future' && t.date && t.date <= tomorrowDate && !t.completed),
    [tasks, tomorrowDate]
  );

  const undatedTasks = useMemo(
    () => tasks.filter((t) => t.category === 'future' && !t.date && !t.completed),
    [tasks]
  );

  const uncompletedToday = useMemo(
    () => tasks.filter((t) => t.category === 'today' && !t.completed),
    [tasks]
  );

  const selectableTasks = useMemo(
    () => [...uncompletedToday, ...undatedTasks],
    [uncompletedToday, undatedTasks]
  );

  const doneItems = [...completedToday, ...completedFuture];

  // アウトプットに含めるタスクのみ（除外されていないもの）
  const visibleDoneItems = useMemo(
    () => doneItems.filter((t) => !excludedOutputIds.includes(t.id)),
    [doneItems, excludedOutputIds]
  );

  // appDateからmm/ddを取得（リアルタイムの日付ではなくアプリ日付を使用）
  const dateParts = safeAppDate.split('-');
  const mm = String(parseInt(dateParts[1], 10));
  const dd = String(parseInt(dateParts[2], 10));

  // 明日該当する定期タスクを「明日やること」に反映
  const recurringTomorrowTasks = useMemo(
    () => getRecurringDueOn(tomorrowDate).map((r) => ({ id: `recur_${r.id}`, title: r.title })),
    [getRecurringDueOn, tomorrowDate]
  );

  const allTomorrowTasks = useMemo(() => {
    const selected = selectableTasks.filter((t) => selectedTomorrowIds.includes(t.id));
    return [...recurringTomorrowTasks, ...autoTomorrowTasks, ...selected];
  }, [recurringTomorrowTasks, autoTomorrowTasks, selectableTasks, selectedTomorrowIds]);

  const previewText = useMemo(() => {
    // アウトプットには除外されていないタスクのみ表示
    const doneLines = visibleDoneItems.map((t) => `\u2705 ${t.title}`).join('\n');
    const tomorrowLines = allTomorrowTasks.map((t) => `\u30fb${t.title}`).join('\n');
    let text = `\u304a\u75b2\u308c\u69d8\u3067\u3059\u2728\n${mm}/${dd} \u30a2\u30a6\u30c8\u30d7\u30c3\u30c8\n\n\u25cf\u4eca\u65e5\u3084\u3063\u305f\u3053\u3068\u25cf\n${doneLines || '\uff08\u306a\u3057\uff09'}`;
    text += `\n\n\u25cf\u660e\u65e5\u3084\u308b\u3053\u3068\u25cf\n${tomorrowLines || '\uff08\u306a\u3057\uff09'}`;
    if (freeText.trim()) {
      text += `\n\n\u25cf\u81ea\u7531\u8a18\u8f09\u25cf\uff08\u611f\u60f3\u306a\u3069\u3042\u308c\u3070\uff09\n${freeText.trim()}`;
    }
    return text;
  }, [visibleDoneItems, allTomorrowTasks, freeText, mm, dd]);

  // タスクの表示/非表示を切り替え（色の濃淡で判別）
  const toggleOutputVisibility = (taskId) => {
    setExcludedOutputIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleTomorrowTask = (taskId) => {
    setSelectedTomorrowIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(previewText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // 確認画面で「今日を終了する」を押した時
  const handleConfirmFinish = () => {
    // 日締め後は翌日のタスクに変わるため、終了時点の結果を保持する。
    setAllTodayCompleted(completedToday.length > 0 && uncompletedToday.length === 0);
    finishDay(freeText, false, selectedTomorrowIds).catch((err) => {
      console.error('finishDay error:', err);
    });
    setFreeText('');
    setStep('done');
    setTimeout(() => {
      handleCloseDone();
    }, 6000);
  };

  const handleCloseDone = () => {
    setCopied(false);
    setStep('edit');
    setSelectedTomorrowIds([]);
    setExcludedOutputIds([]);
    onClose();
  };

  const handleClose = () => {
    setCopied(false);
    setStep('edit');
    setSelectedTomorrowIds([]);
    setExcludedOutputIds([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={step === 'done' ? handleCloseDone : handleClose}
    >
      <div
        className="modal-content"
        onClick={(e) => {
          if (step === 'done') {
            handleCloseDone();
          } else {
            e.stopPropagation();
          }
        }}
      >

        {/* ===== 完了メッセージ（ライオン） ===== */}
        {step === 'done' && (
          <div className="finish-done-screen">
            <img
              src="/lion-end-of-day.png"
              alt=""
              aria-hidden="true"
              className="lion-decoration"
            />
            <div className={`done-message${allTodayCompleted ? ' done-message-all-complete' : ''}`} role="status">
              {allTodayCompleted ? (
                <>全部出来て偉いで！<br />明日も頑張りや！</>
              ) : (
                <>今日もお疲れ様！<br />オスは明日も元気に働くで！！</>
              )}
            </div>
            <p className="done-hint">タップで閉じる</p>
          </div>
        )}

        {/* ===== 確認画面 ===== */}
        {step === 'confirm' && (
          <>
            <div className="modal-header">
              <h2>確認</h2>
              <button className="modal-close" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body confirm-body">
              <p className="confirm-text">今日を終了します。よろしいですか？</p>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setStep('preview')}>
                  戻る
                </button>
                <button
                  className="btn btn-block btn-finish-day"
                  onClick={handleConfirmFinish}
                >
                  今日を終了する
                </button>
              </div>
            </div>
          </>
        )}

        {/* ===== 通常の画面 ===== */}
        {step !== 'confirm' && step !== 'done' && (
          <>
            <div className="modal-header">
              <h2>
                {step === 'edit' && '\u4eca\u65e5\u306e\u30bf\u30b9\u30af\u3092\u7d42\u4e86\u3059\u308b'}
                {step === 'select' && '\u660e\u65e5\u3084\u308b\u3053\u3068\u3092\u9078\u629e'}
                {step === 'preview' && '\u6295\u7a3f\u30d7\u30ec\u30d3\u30e5\u30fc'}
              </h2>
              <button className="modal-close" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {step === 'edit' && (
              <div className="modal-body">
                <div className="finish-section">
                  <h3>
                    <span className="section-icon">{'\u2705'}</span>
                    今日やったこと（{doneItems.length}件）
                  </h3>
                  <p className="output-hint">タップでアウトプットへの表示/非表示を切り替え</p>
                  {doneItems.length > 0 ? (
                    <ul className="finish-list done-list">
                      {doneItems.map((t) => {
                        const isExcluded = excludedOutputIds.includes(t.id);
                        return (
                          <li
                            key={t.id}
                            className={`done-item-toggle ${isExcluded ? 'output-excluded' : 'output-included'}`}
                            onClick={() => toggleOutputVisibility(t.id)}
                          >
                            {t.title}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty-note">完了したタスクはありません</p>
                  )}
                </div>

                <div className="finish-section">
                  <h3>
                    <span className="section-icon">{'\ud83d\udcac'}</span>
                    自由記載
                  </h3>
                  <textarea
                    className="free-text-input"
                    placeholder="今日の感想や気づきなど..."
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    rows={3}
                  />
                </div>

                <button className="btn btn-primary btn-block" onClick={() => setStep('select')}>
                  次へ：明日やることを選択
                </button>
              </div>
            )}

            {step === 'select' && (
              <div className="modal-body">
                {autoTomorrowTasks.length > 0 && (
                  <div className="finish-section">
                    <h3>
                      <span className="section-icon">{'\ud83d\udcc5'}</span>
                      明日の日付のタスク（自動）
                    </h3>
                    <ul className="finish-list future-list">
                      {autoTomorrowTasks.map((t) => (
                        <li key={t.id} className="auto-selected">
                          <span className="auto-check">{'\u2705'}</span>
                          {t.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectableTasks.length > 0 && (
                  <div className="finish-section">
                    <h3>
                      <span className="section-icon">{'\ud83d\udc46'}</span>
                      明日やることに追加する（任意選択）
                    </h3>
                    <ul className="finish-list selectable-list">
                      {selectableTasks.map((t) => (
                        <li
                          key={t.id}
                          className={`selectable-item ${selectedTomorrowIds.includes(t.id) ? 'selected' : ''}`}
                          onClick={() => toggleTomorrowTask(t.id)}
                        >
                          <span className="select-check">
                            {selectedTomorrowIds.includes(t.id) ? '\u2705' : '\u2b1c'}
                          </span>
                          <span className="selectable-title">{t.title}</span>
                          <span className="selectable-category">
                            {t.category === 'today' ? '今日' : '明日以降'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {autoTomorrowTasks.length === 0 && selectableTasks.length === 0 && (
                  <p className="empty-note">選択可能なタスクはありません</p>
                )}

                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setStep('edit')}>
                    戻る
                  </button>
                  <button className="btn btn-primary" onClick={() => setStep('preview')}>
                    プレビューを確認
                  </button>
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="modal-body">
                <div className="preview-box">
                  <pre className="preview-text">{previewText}</pre>
                </div>

                <div className="modal-actions preview-actions">
                  <button className="btn btn-secondary" onClick={() => setStep('select')}>
                    戻って編集
                  </button>
                  <button
                    className={`btn ${copied ? 'btn-success' : 'btn-primary'}`}
                    onClick={handleCopy}
                  >
                    {copied ? 'コピーしました！' : 'コピーする'}
                  </button>
                </div>
                <button
                  className="btn btn-block btn-finish-day"
                  onClick={() => setStep('confirm')}
                >
                  今日を終了する
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
